import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    PopoverSurfaceMotionTransformType,
    PopoverSurfaceMotionConstantType,
    PopoverSurfaceOriginConstantType,
    PopoverSurfaceDynamicStyleType,
    PopoverSurfaceSignsType
} from "./type";
import type {
    OverlayAlignType,
    OverlaySideType
} from "../../helpers/overlay";
import type {
    DirectionType,
    Mutable
} from "../../types";
import {
    getSurfaceStyle
} from "../../helpers/theme/elevation";
import {
    MOTION_SURFACE_SCALE
} from "../../helpers/motion";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    webStyle
} from "../../utils";

export const POPOVER_SURFACE_ENTER_SIGNS: Record<OverlaySideType, PopoverSurfaceSignsType> = {
    right: {
        x: -1,
        y: 0
    },
    bottom: {
        x: 0,
        y: -1
    },
    left: {
        x: 1,
        y: 0
    },
    top: {
        x: 0,
        y: 1
    }
};

export const POPOVER_SURFACE_ORIGIN_X: Record<DirectionType, Record<OverlayAlignType, string>> = {
    ltr: {
        center: "50%",
        start: "0%",
        end: "100%"
    },
    rtl: {
        center: "50%",
        start: "100%",
        end: "0%"
    }
};

export const POPOVER_SURFACE_ORIGIN_SIDE_Y: Record<"bottom" | "top", string> = {
    bottom: "0%",
    top: "100%"
};

export const POPOVER_SURFACE_ORIGIN_SIDE_X: Record<"right" | "left", string> = {
    right: "0%",
    left: "100%"
};

export const POPOVER_SURFACE_ORIGIN_CENTER = "50%";

export const getPopoverSurfaceOrigin = ({
    actualAlign,
    actualSide,
    direction
}: PopoverSurfaceOriginConstantType): string => {
    if(actualSide === "right" || actualSide === "left") {
        return `${POPOVER_SURFACE_ORIGIN_SIDE_X[actualSide]} ${POPOVER_SURFACE_ORIGIN_CENTER}`;
    }

    return `${POPOVER_SURFACE_ORIGIN_X[direction][actualAlign]} ${POPOVER_SURFACE_ORIGIN_SIDE_Y[actualSide]}`;
};

export const getPopoverSurfaceMotion = ({
    actualSide,
    spaces,
    pop
}: PopoverSurfaceMotionConstantType): PopoverSurfaceMotionTransformType => {
    const signs = POPOVER_SURFACE_ENTER_SIGNS[actualSide];

    return {
        translateX: pop.interpolate({
            outputRange: [
                signs.x * spaces.spacingXs,
                0
            ],
            inputRange: [
                0,
                1
            ]
        }),
        translateY: pop.interpolate({
            outputRange: [
                signs.y * spaces.spacingXs,
                0
            ],
            inputRange: [
                0,
                1
            ]
        }),
        scale: pop.interpolate({
            outputRange: [
                MOTION_SURFACE_SCALE,
                1
            ],
            inputRange: [
                0,
                1
            ]
        })
    };
};

const stylesheet = StyleSheet.create({
    backdrop: {
        ...StyleSheet.absoluteFill
    },
    surface: {
        flexDirection: "column",
        position: "absolute",
        left: 0,
        top: 0,
        ...webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        })
    },
    hiddenSurface: {
        pointerEvents: "none"
    }
});

export const useStyles = ({
    availableHeight,
    availableWidth,
    isPositioned,
    shapeConfigs,
    shadowAlpha,
    elevations,
    maxWidth,
    minWidth,
    radiuses,
    borders,
    padding,
    colors,
    isShow,
    spaces,
    width
}: PopoverSurfaceDynamicStyleType) => {
    const styles = {
        surface: {
            ...getSurfaceStyle({
                surface: "popover",
                shadowAlpha,
                elevations,
                borders,
                colors
            }),
            ...getCornerStyle({
                radius: radiuses.surface,
                shapeConfigs
            }),
            padding: spaces[padding]
        } as Mutable<ViewStyle>
    };

    if(width !== undefined) {
        styles.surface.width = width;
    }

    if(minWidth !== undefined) {
        styles.surface.minWidth = minWidth;
    }

    if(maxWidth !== undefined) {
        styles.surface.maxWidth = maxWidth;
    }

    if(isPositioned) {
        styles.surface.maxHeight = availableHeight;
        styles.surface.maxWidth = maxWidth === undefined ? availableWidth : Math.min(maxWidth, availableWidth);
    }

    if(!isShow) {
        Object.assign(styles.surface, stylesheet.hiddenSurface);
    }

    return styles;
};
export default stylesheet;
